import { useEffect, useState } from "react"

import { useGetTenant } from "@/api/useAuth"
import { Form, message as messageToast } from "@/atoms"
import { API_BASE_PATH } from "@/const/default-values"
import { KODEM_SSO_POPUP_SS } from "@/const/local-storage.constants"
import {
  PROVIDER_AUTH_TYPES,
  SAML_SIGN_IN_ERRORS
} from "@/const/provider-auth.constants"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { getSamlSignInError } from "@/helpers/error.helper"
import { getCenteredPopupFeatures, reloadPage } from "@/helpers/window.helpers"
import { useLogger } from "@/hooks/useLogger"
import { useServices } from "@/hooks/useServices"

import type { Params } from "react-router-dom"
import type { SamlSignInError, StateProviders } from "./authenticate.types"

export const shouldLogError = (
  error: undefined | string,
  excludeErrors: string[]
) => {
  if (typeof error !== "string") return true

  return !excludeErrors.some((excludeError) => error.includes(excludeError))
}

// The API returns the login to this origin when it allows it (e.g. a local SPA on staff or dev envs)
export const kodemSsoStartUrl = (slug: string) =>
  `${API_BASE_PATH}${RESOURCES_NAMES.AUTHENTICATION.SSO}/${encodeURIComponent(slug)}/start?returnOrigin=${encodeURIComponent(window.location.origin)}`

const KODEM_SSO_CHANNEL = "kodem-sso"
const KODEM_SSO_SIGNED_IN = "signed-in"
const KODEM_SSO_FAILED = "failed"
const KODEM_SSO_ACK = "ack"
const KODEM_SSO_ACK_TIMEOUT_MS = 1000
const KODEM_SSO_POPUP_WIDTH = 500
const KODEM_SSO_POPUP_HEIGHT = 600
// The page gives up waiting for a popup closed before the ACS, which hands nothing back
export const KODEM_SSO_PENDING_TIMEOUT_MS = 2 * 60 * 1000

let kodemSsoChannel: BroadcastChannel | undefined

// Stops listening for a Kodem SSO popup's outcome
const closeKodemSsoChannel = () => {
  kodemSsoChannel?.close()
  kodemSsoChannel = undefined
}

// Kodem-started SAML login in a popup: the API redirects to the tenant's IdP, which posts back to
// the ACS. IdPs set Cross-Origin-Opener-Policy, which severs the popup from its opener, so the ACS
// hands the outcome back over a same-origin BroadcastChannel rather than window.opener. Resolves
// false when the browser blocked the popup: the login never runs in the sign-in page's window.
export const openKodemSso = (slug: string, onFailed: () => void) => {
  // The popup opens with a copy of this page's session storage, which it keeps through the IdP: the
  // mark tells its ACS not to start the app. The page itself drops the mark right away
  sessionStorage.setItem(KODEM_SSO_POPUP_SS, "true")
  const popup = window.open(
    kodemSsoStartUrl(slug),
    KODEM_SSO_CHANNEL,
    getCenteredPopupFeatures(KODEM_SSO_POPUP_WIDTH, KODEM_SSO_POPUP_HEIGHT)
  )
  sessionStorage.removeItem(KODEM_SSO_POPUP_SS)

  if (!popup) return false

  closeKodemSsoChannel()
  const channel = new BroadcastChannel(KODEM_SSO_CHANNEL)

  kodemSsoChannel = channel
  channel.onmessage = ({ data }) => {
    if (data !== KODEM_SSO_SIGNED_IN && data !== KODEM_SSO_FAILED) return

    channel.postMessage(KODEM_SSO_ACK)
    closeKodemSsoChannel()

    if (data === KODEM_SSO_FAILED) return onFailed()
    // The session lives in the API cookies and local storage, shared with the popup. Reloading the
    // sign-in URL keeps its redirect_url for the app to go on to
    reloadPage()
  }

  return true
}

// The ACS runs in a popup opened by a Kodem SSO sign-in, rather than in a tab (e.g. an IdP-started login)
export const isKodemSsoPopup = () =>
  sessionStorage.getItem(KODEM_SSO_POPUP_SS) === "true"

// Resolves true when a sign-in page that opened the popup took the outcome over, false when there
// is none (the ACS was not reached in a Kodem SSO popup)
const handKodemSsoToOpener = (outcome: string) =>
  new Promise<boolean>((resolve) => {
    const channel = new BroadcastChannel(KODEM_SSO_CHANNEL)
    const done = (handed: boolean) => {
      clearTimeout(timeout)
      channel.close()
      resolve(handed)
    }
    const timeout = setTimeout(() => done(false), KODEM_SSO_ACK_TIMEOUT_MS)

    channel.onmessage = ({ data }) => {
      if (data === KODEM_SSO_ACK) done(true)
    }
    channel.postMessage(outcome)
  })

// Called by the ACS after a Kodem SSO sign-in
export const handKodemSsoSignInToOpener = () =>
  handKodemSsoToOpener(KODEM_SSO_SIGNED_IN)

// Called by the ACS on a failed SSO sign-in, so the error is shown on the sign-in page rather than
// the app loading in the popup
export const handKodemSsoFailureToOpener = () =>
  handKodemSsoToOpener(KODEM_SSO_FAILED)

export const useAuthenticate = (
  params: Params,
  form: ReturnType<typeof Form.useForm>[number]
) => {
  const { slug } = params || {}
  const { auth } = useServices()
  const { logger, EVENTS } = useLogger()
  const [loading, setLoading] = useState(false)
  const [samlError, setSamlError] = useState<SamlSignInError>()
  // A Kodem SSO popup is open: the sign-in page shows the loader until it hands the outcome back
  const [samlPending, setSamlPending] = useState(false)
  const { providers, isKodemAuthBackend } = useProviders(slug)

  // A page the user moved on to (another route, or the app after a password sign-in) is not
  // reloaded by a late outcome: the popup, left without a listener, closes itself
  useEffect(() => closeKodemSsoChannel, [])

  // A popup closed before the ACS hands nothing back: after a while the SAML button comes back with
  // the error, which also stops the automatic sign-in's loader. The channel stays open while the
  // form is, so a sign-in that completes later still takes over the page
  useEffect(() => {
    if (!samlPending) return

    const timeout = setTimeout(() => {
      setSamlPending(false)
      setSamlError(SAML_SIGN_IN_ERRORS["kodem/sso-not-completed"])
    }, KODEM_SSO_PENDING_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [samlPending])

  const setError = () => {
    form.setFields([
      {
        name: "email",
        errors: ["Email or password is incorrect"]
      },
      {
        name: "password",
        errors: [""]
      }
    ])
  }

  return {
    loading,
    providers,
    samlError,
    samlPending,
    signInWithSaml: async () => {
      try {
        const provider = providers?.[PROVIDER_AUTH_TYPES.SAML]

        if (provider && isKodemAuthBackend && slug) {
          const opened = openKodemSso(slug, () => {
            setSamlPending(false)
            setSamlError(SAML_SIGN_IN_ERRORS["kodem/sso-failed"])
          })

          setSamlPending(opened)
          // Like a blocked Firebase popup: the sign-in button then opens it on the user's click
          if (!opened) setSamlError(SAML_SIGN_IN_ERRORS["auth/popup-blocked"])

          return
        }

        if (!provider)
          return logger.error("No SAML Provider", {
            tags: {
              custom_error: EVENTS.ERROR_EVENTS.SIGN_IN,
              provider: "saml"
            }
          })

        const result = await auth.signInWithSaml(provider)

        if (result?.error)
          logger.error("SAML result with error", {
            tags: {
              custom_error: EVENTS.ERROR_EVENTS.SIGN_IN,
              provider: "saml"
            }
          })
      } catch (error: any) {
        const samlSignInError = getSamlSignInError(error)

        if (samlSignInError) return setSamlError(samlSignInError)

        const excludeErrors = [
          "auth/cancelled-popup-request",
          "auth/popup-closed-by-user"
        ]
        const message = error?.message

        if (shouldLogError(message, excludeErrors)) {
          messageToast.error(
            typeof message === "string" ? message : "Something went wrong"
          )
          logger.error(error as Error, {
            tags: {
              custom_error: EVENTS.ERROR_EVENTS.SIGN_IN,
              provider: "saml"
            }
          })
        }
      }
    },
    signInWithGoogle: async () => {
      await auth.signInWithGoogle()
    },
    signInWithGithub: async () => {
      await auth.signInWithGithub()
    },
    signInWithEmailPass: async (values: {
      email: string
      password: string
    }) => {
      try {
        const { email, password } = values || {}

        if (!email || !password) return setError()

        setLoading(true)

        const result = await auth.signInWithEmailPass(email, password)

        if (result?.error) setError()
      } catch (error: any) {
        setError()

        const excludeErrors = ["auth/invalid-credential"]

        if (shouldLogError(error?.message, excludeErrors))
          logger.error(error as Error, {
            tags: {
              custom_error: EVENTS.ERROR_EVENTS.SIGN_IN,
              provider: "email"
            }
          })
      } finally {
        setLoading(false)
      }
    }
  }
}

function useProviders(slug: string | undefined) {
  const { auth, localStorage } = useServices()
  const [providers, setProviders] = useState<null | StateProviders>()
  const [isKodemAuthBackend, setIsKodemAuthBackend] = useState(false)
  const { isLoading, isFetched, response } = useGetTenant(slug)
  const { data } = response || {}

  useEffect(() => {
    const {
      firebaseTenantId,
      identityProviders = [],
      isMultiFactorAuthEnabled,
      isKodemAuthBackend: kodemAuthBackend
    } = data || {}
    const slugAndAuthServiceExists = slug && auth

    // A Kodem-backed tenant may have no Firebase tenant; a tenant without providers gets an
    // empty sign-in form instead of an endless loader
    if (slugAndAuthServiceExists && data) {
      if (firebaseTenantId) auth.setTenantId(firebaseTenantId)
      auth.setCompany(slug)
      auth.setKodemAuthBackend(!!kodemAuthBackend)
      // Multi-factor sign-in is Firebase's, a Kodem-backed tenant has none
      auth.setMultiFactorAuth(!!isMultiFactorAuthEnabled && !kodemAuthBackend)
      setIsKodemAuthBackend(!!kodemAuthBackend)

      setProviders(
        identityProviders.reduce((acc, provider) => {
          acc[provider.type] = provider.id || provider.type

          return acc
        }, {} as StateProviders)
      )
    }
  }, [auth, slug, data, localStorage])

  return { providers, isKodemAuthBackend, isLoading, isFetched }
}
